import { ActionType as AccessTokenActionType } from '~/AccessToken';
import { BgMaxPrice, ProductUpdate, ValidationResult } from '~/Api/User/ProductGroup/BgMaxPrice';

import { Action, ActionTypes } from './Action';

import type { AccessTokenAction } from '~/AccessToken/Action';

export interface BgMaxPriceState {
	bgMaxPrice: BgMaxPrice | null;
	isFetching: boolean;
	request: Promise<any>;
}

type UpdateState =
	| {
			basketVersion: string;
			name: 'scheduled';
			packageSize: number | null;
			historicProductDataViewDate: Date | null;
			products: ProductUpdate[];
			timeoutId: NodeJS.Timeout;
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	isFetching: boolean;
	request: Promise<any>;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]: BgMaxPriceState };
	export: Export | null;
	update: Update | null;
}

export const createState = (): State => ({
	byId: {},
	export: null,
	update: null,
});

// tslint:disable-next-line: cyclomatic-complexity
export const reducer = (state: State = createState(), action: Action | AccessTokenAction): State => {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						bgMaxPrice: state.byId[action.groupId]?.bgMaxPrice ?? null,
						isFetching: true,
						request: action.request,
					},
				},
			};

		case ActionTypes.receive: {
			const bgMaxPrice = state.byId[action.groupId];
			if (bgMaxPrice?.request !== action.request) {
				return state;
			}

			if (action.bgMaxPrice === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...bgMaxPrice,
						bgMaxPrice: action.bgMaxPrice,
						isFetching: false,
					},
				},
			};
		}

		case ActionTypes.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionTypes.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						basketVersion: action.basketVersion,
						name: 'scheduled',
						packageSize: action.packageSize,
						historicProductDataViewDate: action.historicProductDataViewDate,
						products: action.products,
						timeoutId: action.timeoutId,
					},
				},
			};
		}

		case ActionTypes.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionTypes.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			if (action.bgMaxPrice !== null && action.groupId in byId) {
				byId[action.groupId] = {
					...byId[action.groupId],
					bgMaxPrice: action.bgMaxPrice,
				};
			}

			return {
				...state,
				byId,
				export: null,
				update: {
					groupId: action.groupId,
					state: action.bgMaxPrice !== null ? { name: 'updated' } : { name: 'failed' },
				},
			};
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
};

export const selectBgMaxPrice = (state: State, groupId: string): BgMaxPriceState | null => state.byId[groupId];

export const selectExport = (state: State, groupId: string): Export | null =>
	state.export?.groupId === groupId ? state.export : null;
